import * as React from 'react';
import { cn } from '../lib/utils.js';

/**
 * DateRangeBadge — read-only pill for a single fixed date range (e.g. a
 * reservation or waitlist entry's period), NOT an interactive control.
 *
 * Visually mirrors the "period chip" treatment already used on the client
 * offer public-link page (`ClientOfferReviewPeriodChips` in
 * `packages/screens/src/offers/client-offer-review/client-offer-review-period-chips.tsx`,
 * whose color tokens this component's `tone` variants reuse 1:1) so a date
 * range reads the same wherever it appears — CU-86cb3x6y6 follow-up. That
 * component stays app-specific (it also carries selection/klocki domain
 * logic); this is the generic, domain-free "show one formatted range as a
 * badge" piece extracted for reuse (e.g. the reservation waitlist panel).
 *
 * No date parsing/formatting happens here — the caller MUST pre-format
 * `label` (e.g. via `formatDisplayDateRange` in `apps/web/src/lib/date-format.ts`)
 * so every surface keeps rendering dates through the one canonical formatter.
 */

export type DateRangeBadgeTone = 'neutral' | 'reserved' | 'sold';

const TONE_CLASS: Record<DateRangeBadgeTone, string> = {
  // Matches CHIP_CLASS.free in client-offer-review-types.ts.
  neutral: 'bg-background border-border text-muted-foreground',
  // Matches CHIP_CLASS.reserved.
  reserved: 'bg-status-option/15 border-status-option/50 text-status-option',
  // Matches CHIP_CLASS.sold.
  sold: 'bg-status-sold/15 border-status-sold/40 text-status-sold',
};

export interface DateRangeBadgeProps {
  /** Pre-formatted display text, e.g. "1 cze 2026 – 31 sie 2026". */
  label: string;
  tone?: DateRangeBadgeTone;
  className?: string;
  'data-testid'?: string;
}

export function DateRangeBadge({
  label,
  tone = 'neutral',
  className,
  'data-testid': testId,
}: DateRangeBadgeProps) {
  return (
    <span
      data-testid={testId}
      className={cn(
        'inline-flex w-fit items-center whitespace-nowrap rounded-md border px-1.5 py-0.5 text-2xs font-medium tabular-nums',
        TONE_CLASS[tone],
        className,
      )}
    >
      {label}
    </span>
  );
}
